/**
 * 좋은 노드 좌표 계산 알고리즘 도전 코드
 */

import { Vue, Component } from 'vue-property-decorator';

import _ from 'lodash';
import rawData from '@/data/19983881105_e93c2d8279_b.jpg.json';
// import rawData from '@/data/img_237.jpg.json';
// import rawData from '@/data/img_348.jpg.json';

interface Node {
  Ind: number;
  Type: string;
  Lind: number;
  Rind: number;
  NumLeaves: number;
  R: number;
  G: number;
  B: number;
  Ward: number;
  Sse: number;
  Var: number;
  Std: number;
  NumClusterSamples: number;
  x: number | null; // 노드의 x 좌표
  y: number | null; // 노드의 y 좌표
  parentInd: number | null; // 부모 index
  depth: number | null; // root 노드로부터의 depth
  leftRightInfoList: string[]; // root 노드로 부터 왼쪽 자식인지, 오른쪽 자식인지. ['left', 'right', 'left', 'left']
  depthRowIndex: number; // 일정 depth에서의 왼쪽시작 기준으로 해당 node의 index
  depthInterval: number; // 왼쪽 오른쪽 자식의 가로 간격 단위
  leftChildDepthIntervalsDict: ChildDepthIntervalsDict; // depth별 현재 노드 기준으로 왼쪽 자식이 얼마나 떨어져있는지 알려주는 객체
  rightChildDepthIntervalsDict: ChildDepthIntervalsDict; // depth별 현재 노드 기준으로 오른쪽 자식이 얼마나 떨어져있는지 알려주는 객체
}

interface ChildDepthIntervalsDict {
  [depth: number]: number;
}

interface NodesDict {
  [nodeId: number]: Node;
}

interface Edge {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

@Component({})
export default class BinaryTree3 extends Vue {
  private readonly xVariance: number = 8;
  private readonly nodeYInterval: number = 30;

  private nodes: Node[] = [];
  private edges: Edge[] = [];

  private created() {
    // nodes를 초기화한다.
    this.nodes = _.map(rawData, datum => {
      return {
        Ind: datum.Ind,
        Type: datum.Type,
        Lind: datum.Lind,
        Rind: datum.Rind,
        NumLeaves: datum.NumLeaves,
        R: datum.R,
        G: datum.G,
        B: datum.B,
        Ward: datum.Ward,
        Sse: datum.Sse,
        Var: datum.Var,
        Std: datum.Std,
        NumClusterSamples: datum.NumClusterSamples,
        x: null,
        y: null,
        parentInd: null,
        depth: null,
        leftRightInfoList: [],
        depthRowIndex: -1,
        depthInterval: 0,
        leftChildDepthIntervalsDict: {},
        rightChildDepthIntervalsDict: {}
      };
    });

    let nodesDict: NodesDict = _.keyBy(this.nodes, node => node.Ind);

    // parent를 구한다.
    _.forEach(this.nodes, node => {
      if (nodesDict.hasOwnProperty(node.Lind)) {
        nodesDict[node.Lind].parentInd = node.Ind;
      }
      if (nodesDict.hasOwnProperty(node.Rind)) {
        nodesDict[node.Rind].parentInd = node.Ind;
      }
    });
    // root를 구한다.
    const rootNode: Node = _.filter(
      this.nodes,
      node => node.parentInd === null
    )[0];

    rootNode.x = 1000;
    rootNode.y = 50;

    // 각 node마다 depth를 구한다.
    _.forEach(this.nodes, node => {
      // TODO node에 대입하는 코드는 안해도 될듯
      node = this.makeNodeDepthAndLeftRightInfoList(node, nodesDict);
      node.depthRowIndex = this.makeXPositionIndex(node);
    });

    const maxDepthNode: Node = _.maxBy(this.nodes, node => node.depth) as Node;
    const maxDepth: number = maxDepthNode.depth as number;

    // node의 x, y 좌표를 찾는다.
    _.forEach(this.nodes, node => {
      // 부모를 통해 좌표를 찾는다 (부모도 좌표가 없으면 부모의 부모의 좌표를 찾는다.)
      nodesDict = this.positionedNodesDict({
        node,
        nodesDict,
        xVariance: this.xVariance
      });
    });

    _.forEach(this.nodes, node => {
      // 자식 먼저 depth별 왼쪽 자식 interval, 오른쪽 자식 interval을 구한다.
      // 부모때 계산시, 자식들 leftChildDepthInterval, rightChildDepthInterval을 재계산한다.
      // root먼저 좌표를 찾아 타고 내려가면서 leftChildDepthInterval,rightChildDepthInterval을 확인하며 자식 좌표들을 계산한다.
      //
      this.makeChildDepthInterval(node, nodesDict);
    });

    // nodesDict = this.makeNotOverlaped(nodesDict, this.xVariance);

    this.nodes = _.values(nodesDict);

    // edges를 만든다.
    _.forEach(this.nodes, node => {
      if (node.Lind !== 0) {
        this.edges.push({
          x1: node.x as number,
          y1: node.y as number,
          x2: nodesDict[node.Lind].x as number,
          y2: nodesDict[node.Lind].y as number
        });
      }
      if (node.Rind !== 0) {
        this.edges.push({
          x1: node.x as number,
          y1: node.y as number,
          x2: nodesDict[node.Rind].x as number,
          y2: nodesDict[node.Rind].y as number
        });
      }
    });

    // console.log('this.nodes', this.nodes);
    // console.log('this.edges', this.edges);
  }

  private makeXPositionIndex(node: Node): number {
    let xPositionIndex: number = 0;
    _.forEach(node.leftRightInfoList, (info, index) => {
      if (index < node.leftRightInfoList.length - 1) {
        if (info === 'right') {
          xPositionIndex += Math.pow(
            2,
            node.leftRightInfoList.length - 1 - index
          );
        }
      } else {
        if (info === 'right') {
          xPositionIndex += 1;
        }
      }
    });

    return xPositionIndex;
  }

  /**
   * node들이 겹치지 않게 만드는 함수
   * @param nodesDict
   */
  private makeNotOverlaped(nodesDict: NodesDict, xVariance: number): NodesDict {
    let nodesDictVariable = nodesDict;

    let testIndex: number = 0;
    while (testIndex < 10000) {
      const dislocatedPositionNodes = this.findDislocatedPositionNodes(
        nodesDictVariable
      );

      if (dislocatedPositionNodes.length !== 0) {
        const sameParentNode = this.findSameAncestorNode(
          dislocatedPositionNodes[0],
          dislocatedPositionNodes[1],
          nodesDictVariable
        );

        // sameParentNode를 기준으로 왼쪽 오른쪽 이동시킨다.
        nodesDictVariable = this.moveSelfAndDescendants(
          nodesDictVariable[sameParentNode.Rind],
          nodesDictVariable,
          xVariance
        );
        nodesDictVariable = this.moveSelfAndDescendants(
          nodesDictVariable[sameParentNode.Lind],
          nodesDictVariable,
          -xVariance
        );
      } else {
        console.log('break');
        break;
      }
      testIndex++;
    }

    testIndex = 0;
    while (testIndex < 10000) {
      const smallGapPositionNodes: Node[] = this.findSmallGapPositionNodes(
        nodesDictVariable,
        xVariance * 2
      );

      if (smallGapPositionNodes.length !== 0) {
        const sameParentNode = this.findSameAncestorNode(
          smallGapPositionNodes[0],
          smallGapPositionNodes[1],
          nodesDictVariable
        );

        nodesDictVariable = this.moveSelfAndDescendants(
          nodesDictVariable[sameParentNode.Rind],
          nodesDictVariable,
          xVariance / 4
        );
        nodesDictVariable = this.moveSelfAndDescendants(
          nodesDictVariable[sameParentNode.Lind],
          nodesDictVariable,
          -xVariance / 4
        );
      } else {
        console.log('break2');
        break;
      }

      testIndex++;
    }

    return nodesDictVariable;
  }
  private findSmallGapPositionNodes(nodesDict: NodesDict, gap: number): Node[] {
    const smallGapPositionNodes: Node[] = [];

    const keys: string[] = Object.keys(nodesDict);

    for (let i = 0; i < keys.length - 1; i++) {
      for (let j = i + 1; j < keys.length; j++) {
        const node1: Node = nodesDict[keys[i]];
        const node2: Node = nodesDict[keys[j]];
        if (node1.depth === node2.depth) {
          if (
            (node1.depthRowIndex < node2.depthRowIndex &&
              (node2.x as number) - (node1.x as number) < gap) ||
            (node1.depthRowIndex > node2.depthRowIndex &&
              (node1.x as number) - (node2.x as number) < gap)
          ) {
            // console.log('!@#');
            smallGapPositionNodes.push(node1);
            smallGapPositionNodes.push(node2);
          }
        }
      }
    }

    return smallGapPositionNodes;
  }

  /**
   * 자신과 자손들을 오른쪽으로 이동하는 함수
   * @param node
   * @param nodesDict
   */
  private moveSelfAndDescendants(
    node: Node,
    nodesDict: NodesDict,
    variance: number
  ): NodesDict {
    let nodesDictVariable = nodesDict;
    const nodeVaiable = nodesDictVariable[node.Ind];

    (nodeVaiable.x as number) += variance;

    // 자식들이 null이 아닌 경우에는 계속 탐색한다.
    if (nodeVaiable.Rind !== 0) {
      nodesDictVariable = this.moveSelfAndDescendants(
        nodesDictVariable[nodeVaiable.Rind],
        nodesDictVariable,
        variance
      );
    }
    if (nodeVaiable.Lind !== 0) {
      nodesDictVariable = this.moveSelfAndDescendants(
        nodesDictVariable[nodeVaiable.Lind],
        nodesDictVariable,
        variance
      );
    }

    return nodesDictVariable;
  }

  /**
   * 두 노드의 가장 가까운 조상 노드를 찾는다.
   * @param node1
   * @param node2
   */
  private findSameAncestorNode(node1: Node, node2: Node, nodesDict: NodesDict) {
    let sameAncestorNode!: Node;
    let currentNode1: Node = node1;
    let currentNode2: Node = node2;
    while (true) {
      if (currentNode1.parentInd === currentNode2.parentInd) {
        if (currentNode1.parentInd === null) {
          sameAncestorNode = currentNode1;
        } else {
          sameAncestorNode = nodesDict[currentNode1.parentInd];
        }
        break;
      } else {
        // node들이 다른경우 다시 탐색한다.
        currentNode1 = nodesDict[currentNode1.parentInd as number];
        currentNode2 = nodesDict[currentNode2.parentInd as number];
      }
    }

    return sameAncestorNode;
  }

  /**
   * 같은 position을 가지는 node가 있는지 찾는다.
   * @param nodesDict
   */
  private findSamePositionNodes(nodesDict: NodesDict) {
    const positionsDict = {};
    const samePositionNodes: Node[] = [];

    _.forEach(nodesDict, node => {
      const nodePositionId: string = `${node.x}-${node.y}`;
      if (!positionsDict.hasOwnProperty(nodePositionId)) {
        positionsDict[`${node.x}-${node.y}`] = node;
      } else {
        samePositionNodes.push(positionsDict[nodePositionId]);
        samePositionNodes.push(node);
        return false;
      }
    });

    return samePositionNodes;
  }

  /**
   * 같은 depth의 node들 간에 위치가 안맞는 경우 node를 반환하는 함수
   * @param nodesDict
   */
  private findDislocatedPositionNodes(nodesDict: NodesDict) {
    const dislocatedPositionNodes: Node[] = [];

    const keys: string[] = Object.keys(nodesDict);

    for (let i = 0; i < keys.length - 1; i++) {
      for (let j = i + 1; j < keys.length; j++) {
        const node1: Node = nodesDict[keys[i]];
        const node2: Node = nodesDict[keys[j]];

        // if (
        //   (node1.Ind === 203 && node2.Ind === 270) ||
        //   (node1.Ind === 270 && node2.Ind === 203)
        // ) {
        //   console.log('node1', node1);
        //   console.log('node2', node2);
        // }

        if (node1.depth === node2.depth) {
          if (
            (node1.depthRowIndex < node2.depthRowIndex &&
              (node1.x as number) >= (node2.x as number)) ||
            (node1.depthRowIndex > node2.depthRowIndex &&
              (node1.x as number) <= (node2.x as number))
          ) {
            dislocatedPositionNodes.push(node1);
            dislocatedPositionNodes.push(node2);
          }
        }
      }
    }

    return dislocatedPositionNodes;
  }

  private findDepth(node: Node, nodesDict: NodesDict): number {
    let depth: number = 0;
    let currentNode: Node = node;

    while (currentNode.parentInd !== null) {
      depth += 1;
      currentNode = nodesDict[currentNode.parentInd];
    }

    return depth;
  }

  private makeNodeDepthAndLeftRightInfoList(
    node: Node,
    nodesDict: NodesDict
  ): Node {
    let currentNode: Node = node;
    let depth: number = 0;
    const leftRightInfoList: string[] = [];

    while (currentNode.parentInd !== null) {
      depth += 1;
      const parentNode: Node = nodesDict[currentNode.parentInd];

      if (currentNode.Ind === parentNode.Lind) {
        leftRightInfoList.unshift('left');
      } else if (currentNode.Ind === parentNode.Rind) {
        leftRightInfoList.unshift('right');
      }

      currentNode = parentNode;
    }

    node.depth = depth;
    node.leftRightInfoList = leftRightInfoList;

    return node;
  }

  /**
   * 자식들 중 가장 깊은 depth를 반환하는 함수
   * @param node
   * @param nodesDict
   */
  private findChildMaxDepth(node: Node, nodesDict: NodesDict): number {
    let leftChildDepth: number = node.depth as number;
    let rightChildDepth: number = node.depth as number;
    if (node.Lind !== 0) {
      leftChildDepth = this.findChildMaxDepth(nodesDict[node.Lind], nodesDict);
    }
    if (node.Rind !== 0) {
      rightChildDepth = this.findChildMaxDepth(nodesDict[node.Rind], nodesDict);
    }

    return leftChildDepth >= rightChildDepth ? leftChildDepth : rightChildDepth;
  }

  /**
   * 해당 node와 그 부모들의 node에 관한 x, y좌표를 형성한 nodesDict를 반환하는 함수이다.
   */
  private positionedNodesDict(o: {
    node: Node;
    nodesDict: NodesDict;
    xVariance: number;
  }): NodesDict {
    let nodesDictVariable: NodesDict = o.nodesDict;

    if (o.node.parentInd !== null) {
      // 부모 node에 대한 x, y좌표를 먼저 구한다.
      let parentNode = nodesDictVariable[o.node.parentInd];
      if (parentNode.x === null || parentNode.y === null) {
        nodesDictVariable = this.positionedNodesDict({
          node: parentNode,
          nodesDict: nodesDictVariable,
          xVariance: o.xVariance
        });
      }
      parentNode = nodesDictVariable[o.node.parentInd];

      // parent의 왼쪽자식인지 오른쪽자식인지 알아야 한다.
      if (o.node.Ind === parentNode.Lind) {
        o.node.x = (parentNode.x as number) - o.xVariance;
      } else if (o.node.Ind === parentNode.Rind) {
        o.node.x = (parentNode.x as number) + o.xVariance;
      }
      o.node.y = (parentNode.y as number) + 50;

      // o.node.y =
      //   (parentNode.y as number) + Math.log(parentNode.Ward * 100000000000000);
      nodesDictVariable[o.node.Ind] = o.node;
    }

    return nodesDictVariable;
  }

  // 해당 node에 대한 leftChildDepthInterval, rightChildDepthInterval 속성을 만들고 nodesDict로 반환한다.
  private makeChildDepthInterval(node: Node, nodesDict: NodesDict): NodesDict {
    // leafNode면 바로 반환
    // internalNode면 leftChildDepthIntervalsDict, rightChildDepthIntervalsDict가 있는지 확인한다.
    if (node.Lind !== 0 || node.Rind !== 0) {
      // node의 leftChildDepthIntervalsDict, rightChildDepthIntervalsDict가 있는지 확인한다
      // 있으면 그냥 반환. 없으면 새로 생성한다.
      if (
        !_.isEmpty(node.leftChildDepthIntervalsDict) &&
        !_.isEmpty(node.rightChildDepthIntervalsDict)
      ) {
        return nodesDict;
      } else {
        // leftChild, rightChild의 leftChildDepthInterval, rightChildDepthInterval을 먼저 만든다.
        const leftChild: Node = nodesDict[node.Lind];
        const rightChild: Node = nodesDict[node.Rind];
        nodesDict = this.makeChildDepthInterval(leftChild, nodesDict);
        nodesDict = this.makeChildDepthInterval(rightChild, nodesDict);

        // leftChild, rightChild는 지금 leftChildDepthInterval, rightChildDepthInterval을 가지고 있다.
        // node의 leftChildDepthIntervalsDict, rightChildDepthIntervalsDict을 구한다
        node.leftChildDepthIntervalsDict[1] = -1;
        node.rightChildDepthIntervalsDict[1] = 1;

        // 자식들에 대한 leftChildDepthIntervalsDict, rightChildDepthIntervalsDict을 통해
        // 가장 오른쪽, 가장 왼쪽 depthIntervalsDict를 구한다

        // leftChild에 대한 depth별 오른쪽 childDepthIntervalsDict를 가져온다.
        // 결과 값 {1: 1, 2: 0, ..., n: ..} 뎁스는 1부족하다.
        nodesDict = this.findRightDepthIntervalsDict(leftChild, nodesDict);

        // 왼쪽으로 1칸씩 이동한다
        // 결과값 {1: -1, 2: 0, ..., n+1: ..} 모두 완성
      }

      // 부딪히는 부분을 기준으로 node.leftChildDepthIntervalsDict, node.rightChildDepthIntervalsDict를 떼어낸다

      // 자식들도 모두 leftChildDepthIntervalsDict, rightChildDepthIntervalsDict를 재계산한다

      //

      // 부모때 계산시, 자식들 leftChildDepthInterval, rightChildDepthInterval을 재계산한다.
      node.leftChildDepthIntervalsDict = { 1: -1 };

      // 자식에 대한 가장 오른쪽 접근값을 가져온다.

      // 결과 값 {1: -1, 2: 0, ...}

      // o.node.rightChildDepthIntervalsDict = { 1: 1 };
      // nodesDictVariable = this.findLeftDepthIntervalsDict(leftChild, o.nodesDict);

      // const less = Object.keys(left).length <= Object.keys(right).length ? left : right;
      // 돌아가면서 depth가 가장 상충되는 부분을 찾는다.
      // for (let i = 1; i <= Object.keys(less).length; i++) {
      // }

      // depth 중 가장 격차가 큰 애들을 기준으로 2차이 나게 벌린다 (depth 별로 모두 2벌림).
    } else {
      // 자식일 때 는 leftChildDepthInterval, rightChildDepthInterval이 없다.
    }

    return nodesDict;
  }

  // 오른쪽 depth interval들을 구하는 함수
  private findRightDepthIntervalsDict(
    node: Node,
    nodesDict: NodesDict
  ): NodesDict {
    const rightDepthIntervalsDict: ChildDepthIntervalsDict = {};

    // depth별로 가장 오른쪽에 있는 노드의 interval을 찾는다.

    // 자식들을

    // bfs로 탐색하는데 오른쪽으로 탐색한다.

    //

    // 기본적으로 왼쪽자식이냐 오른쪽 자식이냐에 따라 1depth 생성
    if (node.parentInd !== null) {
      const parent = nodesDict[node.parentInd];
      if (node.Ind === parent.Lind) {
        rightDepthIntervalsDict[1] = -1;
      } else if (node.Ind === parent.Rind) {
        rightDepthIntervalsDict[1] = 1;
      }
    }

    // leafNode가 아닐 시 depth별 가장 오른쪽 interval 계산
    if (node.Lind !== 0 || node.Rind !== 0) {
      // 1depth마다 탐색한다
      nodesDict = this.findRightDepthIntervalsDict(
        nodesDict[node.Lind],
        nodesDict
      );
      const left = nodesDict[node.Ind].leftChildDepthIntervalsDict;
      nodesDict = this.findRightDepthIntervalsDict(
        nodesDict[node.Rind],
        nodesDict
      );
      const right = nodesDict[node.Ind].rightChildDepthIntervalsDict;
      const array =
        Object.keys(left).length >= Object.keys(right).length ? left : right;
      for (let i = 1; i <= Object.keys(array).length; i++) {
        if (!_.isNil(left[i]) && !_.isNil(right[i])) {
          rightDepthIntervalsDict[i + 1] =
            left[i] >= right[i] ? left[i] : right[i];
        } else if (_.isNil(left[i])) {
          rightDepthIntervalsDict[i + 1] = right[i];
        } else if (_.isNil(right[i])) {
          rightDepthIntervalsDict[i + 1] = left[i];
        }
      }

      // 왼쪽 자식이냐 오른쪽 자식이냐에 따라 interval이 1단위 변화함
      if (node.parentInd !== null) {
        const parent = nodesDict[node.parentInd];
        if (node.Ind === parent.Lind) {
          for (
            let i = 2;
            i <= Object.keys(rightDepthIntervalsDict).length;
            i++
          ) {
            rightDepthIntervalsDict[i] -= 1;
          }
        } else if (node.Ind === parent.Rind) {
          for (
            let i = 2;
            i <= Object.keys(rightDepthIntervalsDict).length;
            i++
          ) {
            rightDepthIntervalsDict[i] += 1;
          }
        }
      }
    }

    node.leftChildDepthIntervalsDict = rightDepthIntervalsDict;

    return nodesDict;
  }

  // 오른쪽 depth interval들을 구하는 함수. 거의 차이 없네. 'left', 'right' 부등호만 바꾸면 됨.
  private findLeftDepthIntervalsDict(node: Node, nodesDict: NodesDict) {
    const rightDepthIntervalsDict: ChildDepthIntervalsDict = {};

    // 기본적으로 왼쪽자식이냐 오른쪽 자식이냐에 따라 1depth 생성
    if (node.parentInd !== null) {
      const parent = nodesDict[node.parentInd];
      if (node.Ind === parent.Lind) {
        rightDepthIntervalsDict[1] = -1;
      } else if (node.Ind === parent.Rind) {
        rightDepthIntervalsDict[1] = 1;
      }
    }

    // leafNode가 아닐 시 depth별 가장 오른쪽 interval 계산
    if (node.Lind !== 0 || node.Rind !== 0) {
      // 1depth마다 탐색한다
      const left = this.findLeftDepthIntervalsDict(
        nodesDict[node.Lind],
        nodesDict
      );
      const right = this.findLeftDepthIntervalsDict(
        nodesDict[node.Rind],
        nodesDict
      );
      const array =
        Object.keys(left).length >= Object.keys(right).length ? left : right;
      for (let i = 1; i <= Object.keys(array).length; i++) {
        if (!_.isNil(left[i]) && !_.isNil(right[i])) {
          rightDepthIntervalsDict[i + 1] =
            left[i] <= right[i] ? left[i] : right[i];
        } else if (_.isNil(left[i])) {
          rightDepthIntervalsDict[i + 1] = right[i];
        } else if (_.isNil(right[i])) {
          rightDepthIntervalsDict[i + 1] = left[i];
        }
      }

      // 왼쪽 자식이냐 오른쪽 자식이냐에 따라 interval이 1단위 변화함
      if (node.parentInd !== null) {
        const parent = nodesDict[node.parentInd];
        if (node.Ind === parent.Lind) {
          for (
            let i = 2;
            i <= Object.keys(rightDepthIntervalsDict).length;
            i++
          ) {
            rightDepthIntervalsDict[i] -= 1;
          }
        } else if (node.Ind === parent.Rind) {
          for (
            let i = 2;
            i <= Object.keys(rightDepthIntervalsDict).length;
            i++
          ) {
            rightDepthIntervalsDict[i] += 1;
          }
        }
      }
    }

    return rightDepthIntervalsDict;
  }

  private findMostRightInterval(node: Node, nodesDict: NodesDict) {
    //
  }

  /**
   * 해당 node와 그 부모들의 node에 관한 x, y좌표를 형성한 nodesDict를 반환하는 함수이다.
   */
  private makeChildrenHorizonLength(
    node: Node,
    nodesDict: NodesDict
  ): NodesDict {
    let nodesDictVariable: NodesDict = nodesDict;

    // 자식 node에 대한 childrenHorizonLength를 먼저 구한다.
    if (node.Lind !== 0 && node.Rind !== 0) {
      const leftChildNode = nodesDictVariable[node.Lind];
      const rightChildNode = nodesDictVariable[node.Rind];
      nodesDictVariable = this.makeChildrenHorizonLength(
        leftChildNode,
        nodesDictVariable
      );
      nodesDictVariable = this.makeChildrenHorizonLength(
        rightChildNode,
        nodesDictVariable
      );

      const mostRightInfoOfLeftChild = ['left', 'right'];
      const mostLeftInfoOfRightChild = ['right', 'left'];
      // 함수 하나 만들어야 겠네

      // if (
      //   leftChildLeftCount <= leftChildRightCount &&
      //   rightChildLeftCount >= rightChildRightCount
      // ) {
      //   // depth마다 몇번 중첩되는지 확인한다.
      //   // 각 depth마다
      // }
    }

    return nodesDictVariable;
  }

  private fillNodeTextColor(node: Node) {
    // if (node.Ind === 7 || node.Ind === 11 || node.Ind === 238) {
    //   return 'rgb(0, 0, 255)';
    // } else {
    //   return 'rgba(0, 0, 0, 0)';
    // }

    return 'rgba(0, 0, 0, 1)';
  }

  private mounted() {
    //
  }
}
